import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { newsList, newsCategories as newsCategoryList } from "@/mocks/news";

export type Movie = (typeof movies)[number];
export type Category = (typeof categories)[number];
export type NewsItem = (typeof newsList)[number];

export const allMovies: Movie[] = movies;
export const allCategories: Category[] = categories;
export const allNews: NewsItem[] = newsList;
export const newsCategories: string[] = newsCategoryList;

export type SortKey = "latest" | "popular" | "rating";

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "按发布时间" },
  { key: "popular", label: "按热度" },
  { key: "rating", label: "按评分" },
];

export function formatDate(value: string): string {
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  return `${parts[0]} 年 ${Number(parts[1])} 月 ${Number(parts[2])} 日`;
}

export function shortDate(value: string): string {
  return value.replace(/-/g, ".");
}

export function getMovieById(id: string | undefined): Movie | undefined {
  return allMovies.find((movie) => movie.id === id);
}

export function getCategoryBySlug(slug: string | undefined): Category | undefined {
  return allCategories.find((category) => category.slug === slug);
}

export function getNewsById(id: string | undefined): NewsItem | undefined {
  return allNews.find((item) => item.id === id);
}

export function getCategoryName(slugs: string[]): string {
  return slugs
    .map((slug) => allCategories.find((category) => category.slug === slug)?.name || slug)
    .join(" · ");
}

export function latestMovies(limit?: number): Movie[] {
  const sorted = [...allMovies].sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function hotMovies(limit?: number): Movie[] {
  const sorted = [...allMovies].sort((a, b) => b.popularity - a.popularity);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export const yearOptions: string[] = Array.from(new Set(allMovies.map((movie) => String(movie.year)))).sort(
  (a, b) => Number(b) - Number(a),
);

export const regionOptions: string[] = Array.from(new Set(allMovies.map((movie) => movie.region)));

export const styleOptions = allCategories.map((category) => ({
  slug: category.slug,
  name: category.name,
}));

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const copy = [...list];
  if (key === "popular") return copy.sort((a, b) => b.popularity - a.popularity);
  if (key === "rating") return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
}

export function filterMovies(
  base: Movie[],
  filters: { years?: string[]; regions?: string[]; styles?: string[] },
): Movie[] {
  const years = filters.years || [];
  const regions = filters.regions || [];
  const styles = filters.styles || [];
  return base.filter((movie) => {
    const yearOk = years.length === 0 || years.includes(String(movie.year));
    const regionOk = regions.length === 0 || regions.includes(movie.region);
    const styleOk = styles.length === 0 || movie.categories.some((slug) => styles.includes(slug));
    return yearOk && regionOk && styleOk;
  });
}

export function moviesOfCategory(slug: string): Movie[] {
  return allMovies.filter((movie) => movie.categories.includes(slug));
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allMovies.filter((movie) => {
    const haystack = [movie.title, movie.titleJa, movie.director, movie.region, ...movie.cast, ...movie.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function searchNews(keyword: string): NewsItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allNews.filter((item) => {
    const body = item.sections.map((section) => section.text).join(" ");
    const haystack = [item.title, item.excerpt, item.category, item.author, ...item.tags, body].join(" ").toLowerCase();
    return haystack.includes(q);
  });
}

export function relatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const shared = item.categories.filter((slug) => movie.categories.includes(slug)).length;
      return { item, score: shared * 10 + item.popularity / 100 };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function relatedNews(current: NewsItem, limit = 3): NewsItem[] {
  const sameCategory = allNews.filter((item) => item.id !== current.id && item.category === current.category);
  const others = allNews.filter((item) => item.id !== current.id && item.category !== current.category);
  return [...sameCategory, ...others].slice(0, limit);
}